import React, { useState, useEffect } from 'react';
import AreaSearchModal from './AreaSearchModal';
import RemoteFilterModal from './RemoteFilterModal';
import SkillsFilterModal from './SkillsFilterModal';
import OccupationsFilterModal from './OccupationsFilterModal';
import IndustriesFilterModal from './IndustriesFilterModal';
import WorkDaysFilterModal from './WorkDaysFilterModal';
import CharacteristicsFilterModal from './CharacteristicsFilterModal';

interface MobileSearchModalProps {
  show: boolean;
  onClose: () => void;
  onSearch: (filters: {
    keyword: string;
    prefecture_ids: number[];
    price_over?: number;
    is_recruiting: boolean;
    remote_types: string[];
    skill_ids: number[];
    occupation_ids: number[];
    industry_ids: number[];
    workday_types: string[];
    characteristic_ids: number[];
    job_source_ids?: number[];
  }) => void;
  initialFilters: {
    keyword: string;
    prefecture_ids: number[];
    price_over?: number;
    is_recruiting: boolean;
    remote_types: string[];
    skill_ids: number[];
    occupation_ids: number[];
    industry_ids: number[];
    workday_types: string[];
    characteristic_ids: number[];
    job_source_ids?: number[];
  };
  skillNames?: {[key: number]: string};
  occupationNames?: {[key: number]: string};
  industryNames?: {[key: number]: string};
  characteristicNames?: {[key: number]: string};
  prefectureNames?: {[key: number]: string};
  jobSourceNames?: {[key: number]: string};
}

const MobileSearchModal: React.FC<MobileSearchModalProps> = ({
  show,
  onClose,
  onSearch,
  initialFilters,
  skillNames = {},
  occupationNames = {},
  industryNames = {},
  characteristicNames = {},
  prefectureNames = {},
  jobSourceNames = {}
}) => {
  const [inputValue, setInputValue] = useState(initialFilters.keyword);
  const [selectedPrefectureIds, setSelectedPrefectureIds] = useState<number[]>(initialFilters.prefecture_ids);
  const [selectedPriceOver, setSelectedPriceOver] = useState<number | undefined>(initialFilters.price_over);
  const [recruitingOnly, setRecruitingOnly] = useState(initialFilters.is_recruiting);
  const [selectedRemoteTypes, setSelectedRemoteTypes] = useState<string[]>(initialFilters.remote_types);
  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>(initialFilters.skill_ids);
  const [selectedOccupationIds, setSelectedOccupationIds] = useState<number[]>(initialFilters.occupation_ids);
  const [selectedIndustryIds, setSelectedIndustryIds] = useState<number[]>(initialFilters.industry_ids);
  const [selectedWorkdayTypes, setSelectedWorkdayTypes] = useState<string[]>(initialFilters.workday_types);
  const [selectedCharacteristicIds, setSelectedCharacteristicIds] = useState<number[]>(initialFilters.characteristic_ids);
  const [selectedCharacteristicNames, setSelectedCharacteristicNames] = useState<string[]>([]);
  const [isLoadingCharacteristics, setIsLoadingCharacteristics] = useState(false);
  
  // 都道府県名を管理するstate
  const [prefectureNamesMap, setPrefectureNamesMap] = useState<{[key: number]: string}>(prefectureNames || {});
  
  // 求人数表示用のstate
  const [jobCount, setJobCount] = useState<number | null>(null);
  const [isLoadingJobCount, setIsLoadingJobCount] = useState(false);
  const [jobCountError, setJobCountError] = useState(false);
  
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [showRemoteModal, setShowRemoteModal] = useState(false);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [showOccupationsModal, setShowOccupationsModal] = useState(false);
  const [showIndustriesModal, setShowIndustriesModal] = useState(false);
  const [showWorkdaysModal, setShowWorkdaysModal] = useState(false);
  const [showCharacteristicsModal, setShowCharacteristicsModal] = useState(false);

  // 初期値の同期
  useEffect(() => {
    setInputValue(initialFilters.keyword);
    setSelectedPrefectureIds(initialFilters.prefecture_ids);
    setSelectedPriceOver(initialFilters.price_over);
    setRecruitingOnly(initialFilters.is_recruiting);
    setSelectedRemoteTypes(initialFilters.remote_types);
    setSelectedSkillIds(initialFilters.skill_ids);
    setSelectedOccupationIds(initialFilters.occupation_ids);
    setSelectedIndustryIds(initialFilters.industry_ids);
    setSelectedWorkdayTypes(initialFilters.workday_types);
    setSelectedCharacteristicIds(initialFilters.characteristic_ids);
  }, [initialFilters]);

  // 都道府県名を取得
  useEffect(() => {
    if (selectedPrefectureIds.length > 0) {
      // 既に取得済みの都道府県名があるかチェック
      const missingIds = selectedPrefectureIds.filter(id => !prefectureNamesMap[id] && !prefectureNames[id]);
      
      if (missingIds.length > 0) {
        fetch('/api/v1/prefectures')
          .then(response => response.json())
          .then(data => {
            setPrefectureNamesMap(prev => {
              const newNamesMap: {[key: number]: string} = { ...prev };
              selectedPrefectureIds.forEach(id => {
                const prefecture = data.find((p: any) => p.id === id);
                if (prefecture && !newNamesMap[id] && !prefectureNames[id]) {
                  newNamesMap[id] = prefecture.name;
                }
              });
              return newNamesMap;
            });
          })
          .catch(error => {
            console.error('Failed to fetch prefecture names:', error);
          });
      }
    }
  }, [selectedPrefectureIds]);

  // 特徴の名前を取得
  useEffect(() => {
    if (selectedCharacteristicIds.length > 0) {
      setIsLoadingCharacteristics(true);
      fetch('/api/v1/characteristics')
        .then(response => response.json())
        .then(data => {
          const names = selectedCharacteristicIds.map(id => {
            const characteristic = data.find((c: any) => c.id === id);
            return characteristic ? characteristic.name : '';
          }).filter(name => name);
          setSelectedCharacteristicNames(names);
          setIsLoadingCharacteristics(false);
        })
        .catch(error => {
          console.error('Failed to fetch characteristic names:', error);
          setIsLoadingCharacteristics(false);
        });
    } else {
      setSelectedCharacteristicNames([]);
    }
  }, [selectedCharacteristicIds]);

  // 検索条件をAPIパラメータに変換する関数
  const buildSearchParams = () => {
    const params = new URLSearchParams();
    
    if (inputValue.trim()) {
      params.append('keyword', inputValue.trim());
    }
    
    selectedPrefectureIds.forEach(id => {
      params.append('prefecture_ids[]', id.toString());
    });
    
    if (selectedPriceOver) {
      params.append('price_over', selectedPriceOver.toString());
    }
    
    if (recruitingOnly) {
      params.append('is_recruiting', 'true');
    }
    
    selectedRemoteTypes.forEach(type => {
      params.append('remote_types[]', type);
    });
    
    selectedSkillIds.forEach(id => {
      params.append('skill_ids[]', id.toString());
    });
    
    selectedOccupationIds.forEach(id => {
      params.append('occupation_ids[]', id.toString());
    });
    
    selectedIndustryIds.forEach(id => {
      params.append('industry_ids[]', id.toString());
    });
    
    selectedWorkdayTypes.forEach(type => {
      params.append('workday_types[]', type);
    });
    
    selectedCharacteristicIds.forEach(id => {
      params.append('characteristic_ids[]', id.toString());
    });
    
    // 件数のみを取得するため、limit=1を指定
    params.append('limit', '1');
    
    return params;
  };

  // 求人数を取得する関数
  const fetchJobCount = async () => {
    const params = buildSearchParams();
    const queryString = params.toString();
    
    setIsLoadingJobCount(true);
    setJobCountError(false);
    
    try {
      const response = await fetch(`/api/v1/job_listings/search?${queryString}`);
      if (!response.ok) {
        throw new Error('Failed to fetch job count');
      }
      const data = await response.json();
      const count = data.pagination?.total_count || data.total_job_listings_count || 0;
      setJobCount(count);
      setJobCountError(false);
    } catch (error) {
      console.error('Failed to fetch job count:', error);
      setJobCountError(true);
      setJobCount(null);
    } finally {
      setIsLoadingJobCount(false);
    }
  };

  // 検索条件変更の監視とデバウンス処理
  useEffect(() => {
    // モーダルが表示されていない場合は何もしない
    if (!show) {
      return;
    }
    
    // デバウンス処理（300ms）
    const timer = setTimeout(() => {
      fetchJobCount();
    }, 300);
    
    // クリーンアップ関数でタイマーをクリア
    return () => {
      clearTimeout(timer);
    };
  }, [
    show,
    inputValue,
    selectedPrefectureIds,
    selectedPriceOver,
    recruitingOnly,
    selectedRemoteTypes,
    selectedSkillIds,
    selectedOccupationIds,
    selectedIndustryIds,
    selectedWorkdayTypes,
    selectedCharacteristicIds
  ]);

  const handleSearch = () => {
    onSearch({
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds,
      job_source_ids: []
    });
    onClose();
  };

  const handleClear = () => {
    setInputValue('');
    setSelectedPrefectureIds([]);
    setSelectedPriceOver(undefined);
    setRecruitingOnly(false);
    setSelectedRemoteTypes([]);
    setSelectedSkillIds([]);
    setSelectedOccupationIds([]);
    setSelectedIndustryIds([]);
    setSelectedWorkdayTypes([]);
    setSelectedCharacteristicIds([]);
    setSelectedCharacteristicNames([]);
  };

  const handleAreaApply = (prefIds: number[]) => {
    setSelectedPrefectureIds(prefIds);
  };

  const handleRemoteApply = (remoteTypes: string[]) => {
    setSelectedRemoteTypes(remoteTypes);
  };

  const handleSkillsApply = (skillIds: number[]) => {
    setSelectedSkillIds(skillIds);
  };

  const handleOccupationsApply = (occupationIds: number[]) => {
    setSelectedOccupationIds(occupationIds);
  };

  const handleIndustriesApply = (industryIds: number[]) => {
    setSelectedIndustryIds(industryIds);
  };

  const handleWorkdaysApply = (workdayTypes: string[]) => {
    setSelectedWorkdayTypes(workdayTypes);
  };

  const handleCharacteristicsApply = (characteristicIds: number[]) => {
    setSelectedCharacteristicIds(characteristicIds);
    
    // 特徴の名前を取得
    if (characteristicIds.length > 0) {
      setIsLoadingCharacteristics(true);
      fetch('/api/v1/characteristics')
        .then(response => response.json())
        .then(data => {
          const names = characteristicIds.map(id => {
            const characteristic = data.find((c: any) => c.id === id);
            return characteristic ? characteristic.name : '';
          }).filter(name => name);
          setSelectedCharacteristicNames(names);
          setIsLoadingCharacteristics(false);
        })
        .catch(error => {
          console.error('Failed to fetch characteristic names:', error);
          setIsLoadingCharacteristics(false);
        });
    } else {
      setSelectedCharacteristicNames([]);
    }
  };

  // 価格帯オプション（8段階）
  const priceOptions = [
    { value: '', label: '単価' },
    { value: '300000', label: '30万円～' },
    { value: '400000', label: '40万円～' },
    { value: '500000', label: '50万円～' },
    { value: '600000', label: '60万円～' },
    { value: '700000', label: '70万円～' },
    { value: '800000', label: '80万円～' },
    { value: '900000', label: '90万円～' },
    { value: '1000000', label: '100万円～' }
  ];

  if (!show) {
    return null;
  }

  return (
    <>
      <div 
        className="modal fade show d-block"
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          zIndex: 10050,
          backgroundColor: 'rgba(0,0,0,0.5)'
        }}
        onClick={(e) => {
          // バックドロップをクリックした時のみ閉じる
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div 
          className="modal-dialog modal-fullscreen"
          style={{ margin: 0, maxWidth: 'none', height: '100%', zIndex: 10051 }}
          onClick={(e) => {
            // モーダルコンテンツ内のクリックは伝播させない
            e.stopPropagation();
          }}
        >
          <div className="modal-content" style={{ height: '100%', border: 'none', borderRadius: 0 }}>
            {/* ヘッダー */}
            <div className="modal-header bg-primary text-white px-4" style={{ position: 'sticky', top: 0, zIndex: 10052 }}>
              <h5 className="modal-title fw-bold">検索条件</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
              ></button>
            </div>

            {/* ボディ - スクロール可能 */}
            <div className="modal-body p-4" style={{ overflowY: 'auto', height: 'calc(100% - 120px)' }}>
              <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
                {/* キーワード検索 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">キーワード</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="キーワードで検索"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                    />
                  </div>
                </div>

                {/* エリア選択 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">勤務地</label>
                  <div>
                    <button
                      type="button"
                      className="form-select text-start"
                      onClick={() => {
                        setShowAreaModal(true);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {selectedPrefectureIds.length > 0 
                        ? `選択済み (${selectedPrefectureIds.length})` 
                        : 'エリアを選択'
                      }
                    </button>
                    {selectedPrefectureIds.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-1 justify-content-end">
                        {selectedPrefectureIds.slice(0, 5).map((id) => {
                          const name = prefectureNamesMap[id] || prefectureNames[id];
                          return name ? (
                            <span key={id} className="badge bg-warning text-dark" style={{ fontSize: '0.7em' }}>
                              {name}
                              <button
                                type="button"
                                className="btn-close btn-close-white ms-1"
                                style={{ fontSize: '0.5em' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAreaApply(selectedPrefectureIds.filter(pid => pid !== id));
                                }}
                                aria-label={`${name}を削除`}
                              ></button>
                            </span>
                          ) : null;
                        })}
                        {selectedPrefectureIds.length > 5 && (
                          <span className="badge bg-warning text-dark" style={{ fontSize: '0.7em' }}>
                            +{selectedPrefectureIds.length - 5}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* 価格下限 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">単価</label>
                  <select
                    className="form-select"
                    value={selectedPriceOver || ''}
                    onChange={(e) => setSelectedPriceOver(e.target.value ? Number(e.target.value) : undefined)}
                  >
                    {priceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 募集中 */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label fw-bold mb-0">募集中のみ</label>
                    <div className="form-check form-switch d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="recruitingOnly"
                        checked={recruitingOnly}
                        onChange={(e) => setRecruitingOnly(e.target.checked)}
                        style={{ 
                          marginTop: 0,
                          width: '3rem',
                          height: '1.5rem',
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 勤務形態 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">勤務形態</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-select text-start"
                      onClick={() => setShowRemoteModal(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      {selectedRemoteTypes.length > 0 
                        ? `選択済み (${selectedRemoteTypes.length})` 
                        : '勤務形態を選択'
                      }
                    </button>
                    {selectedRemoteTypes.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-1 justify-content-end">
                        {selectedRemoteTypes.slice(0, 3).map((type) => {
                          const label = type === 'remote' ? 'リモートOK' : 
                                       type === 'full_remote' ? 'フルリモート' : 
                                       type === 'onsite' ? '常駐' : type;
                          return (
                            <span key={type} className="badge bg-info" style={{ fontSize: '0.7em' }}>
                              {label}
                              <button
                                type="button"
                                className="btn-close btn-close-white ms-1"
                                style={{ fontSize: '0.5em' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoteApply(selectedRemoteTypes.filter(t => t !== type));
                                }}
                                aria-label={`${label}を削除`}
                              ></button>
                            </span>
                          );
                        })}
                        {selectedRemoteTypes.length > 3 && (
                          <span className="badge bg-info" style={{ fontSize: '0.7em' }}>
                            +{selectedRemoteTypes.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* スキル */}
                <div className="mb-4">
                  <label className="form-label fw-bold">スキル</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-select text-start"
                      onClick={() => setShowSkillsModal(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      {selectedSkillIds.length > 0 
                        ? `選択済み (${selectedSkillIds.length})` 
                        : 'スキルを選択'
                      }
                    </button>
                    {selectedSkillIds.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-1 justify-content-end">
                        {selectedSkillIds.slice(0, 3).map((id) => {
                          const name = skillNames[id];
                          return name ? (
                            <span key={id} className="badge bg-secondary" style={{ fontSize: '0.7em' }}>
                              {name}
                              <button
                                type="button"
                                className="btn-close btn-close-white ms-1"
                                style={{ fontSize: '0.5em' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSkillsApply(selectedSkillIds.filter(sid => sid !== id));
                                }}
                                aria-label={`${name}を削除`}
                              ></button>
                            </span>
                          ) : null;
                        })}
                        {selectedSkillIds.length > 3 && (
                          <span className="badge bg-secondary" style={{ fontSize: '0.7em' }}>
                            +{selectedSkillIds.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* 職種 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">職種</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-select text-start"
                      onClick={() => setShowOccupationsModal(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      {selectedOccupationIds.length > 0 
                        ? `選択済み (${selectedOccupationIds.length})` 
                        : '職種を選択'
                      }
                    </button>
                    {selectedOccupationIds.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-1 justify-content-end">
                        {selectedOccupationIds.slice(0, 3).map((id) => {
                          const name = occupationNames[id];
                          return name ? (
                            <span key={id} className="badge bg-secondary" style={{ fontSize: '0.7em' }}>
                              {name}
                              <button
                                type="button"
                                className="btn-close btn-close-white ms-1"
                                style={{ fontSize: '0.5em' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOccupationsApply(selectedOccupationIds.filter(oid => oid !== id));
                                }}
                                aria-label={`${name}を削除`}
                              ></button>
                            </span>
                          ) : null;
                        })}
                        {selectedOccupationIds.length > 3 && (
                          <span className="badge bg-secondary" style={{ fontSize: '0.7em' }}>
                            +{selectedOccupationIds.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* 業界 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">業界</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-select text-start"
                      onClick={() => setShowIndustriesModal(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      {selectedIndustryIds.length > 0 
                        ? `選択済み (${selectedIndustryIds.length})` 
                        : '業界を選択'
                      }
                    </button>
                    {selectedIndustryIds.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-1 justify-content-end">
                        {selectedIndustryIds.slice(0, 3).map((id) => {
                          const name = industryNames[id];
                          return name ? (
                            <span key={id} className="badge bg-secondary" style={{ fontSize: '0.7em' }}>
                              {name}
                              <button
                                type="button"
                                className="btn-close btn-close-white ms-1"
                                style={{ fontSize: '0.5em' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleIndustriesApply(selectedIndustryIds.filter(iid => iid !== id));
                                }}
                                aria-label={`${name}を削除`}
                              ></button>
                            </span>
                          ) : null;
                        })}
                        {selectedIndustryIds.length > 3 && (
                          <span className="badge bg-secondary" style={{ fontSize: '0.7em' }}>
                            +{selectedIndustryIds.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* 週日数 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">週日数</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-select text-start"
                      onClick={() => setShowWorkdaysModal(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      {selectedWorkdayTypes.length > 0 
                        ? `選択済み (${selectedWorkdayTypes.length})` 
                        : '週日数を選択'
                      }
                    </button>
                    {selectedWorkdayTypes.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-1 justify-content-end">
                        {selectedWorkdayTypes.slice(0, 3).map((type) => {
                          const label = type === 'five_days_in_a_week' ? '週5日' :
                                       type === 'four_days_in_a_week' ? '週4日' :
                                       type === 'three_days_in_a_week' ? '週3日' :
                                       type === 'two_days_in_a_week' ? '週2日' :
                                       type === 'one_day_in_a_week' ? '週1日' : type;
                          return (
                            <span key={type} className="badge bg-success" style={{ fontSize: '0.7em' }}>
                              {label}
                              <button
                                type="button"
                                className="btn-close btn-close-white ms-1"
                                style={{ fontSize: '0.5em' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleWorkdaysApply(selectedWorkdayTypes.filter(t => t !== type));
                                }}
                                aria-label={`${label}を削除`}
                              ></button>
                            </span>
                          );
                        })}
                        {selectedWorkdayTypes.length > 3 && (
                          <span className="badge bg-success" style={{ fontSize: '0.7em' }}>
                            +{selectedWorkdayTypes.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* 特徴 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">特徴</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-select text-start"
                      onClick={() => setShowCharacteristicsModal(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      {selectedCharacteristicIds.length > 0 
                        ? `選択済み (${selectedCharacteristicIds.length})` 
                        : '特徴を選択'
                      }
                    </button>
                    
                    {/* 選択された特徴の表示 */}
                    {selectedCharacteristicIds.length > 0 && (
                      <div className="mt-2">
                        {isLoadingCharacteristics ? (
                          <div className="d-flex align-items-center">
                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                              <span className="visually-hidden">読み込み中...</span>
                            </div>
                            <span className="text-muted small">特徴を読み込み中...</span>
                          </div>
                        ) : (
                          <div className="d-flex flex-wrap gap-1 justify-content-end">
                            {selectedCharacteristicNames.map((name, index) => (
                              <span key={index} className="badge bg-secondary">
                                {name}
                                <button
                                  type="button"
                                  className="btn-close btn-close-white ms-1"
                                  style={{ fontSize: '0.6em' }}
                                  onClick={() => {
                                    const newIds = selectedCharacteristicIds.filter((_, i) => i !== index);
                                    const newNames = selectedCharacteristicNames.filter((_, i) => i !== index);
                                    setSelectedCharacteristicIds(newIds);
                                    setSelectedCharacteristicNames(newNames);
                                    handleCharacteristicsApply(newIds);
                                  }}
                                ></button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* フッター */}
            <div className="modal-footer bg-light border-top px-4" style={{ position: 'sticky', bottom: 0, zIndex: 10052 }}>
              {/* 求人数表示 */}
              {!jobCountError && (isLoadingJobCount || jobCount !== null) && (
                <div className="w-100 mb-2 text-center">
                  {isLoadingJobCount ? (
                    <div className="d-flex align-items-center justify-content-center">
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                        <span className="visually-hidden">読み込み中...</span>
                      </div>
                      <span className="text-muted small">読み込み中...</span>
                    </div>
                  ) : jobCount !== null ? (
                    <div className="text-muted small">
                      現在の検索条件の求人数{' '}
                      <span style={{ 
                        fontSize: '1.2em', 
                        fontWeight: 'bold', 
                        color: '#dc3545' 
                      }}>
                        {jobCount.toLocaleString()}
                      </span>
                      件
                    </div>
                  ) : null}
                </div>
              )}
              <div className="d-flex gap-2 w-100">
                <button
                  type="button"
                  className="btn btn-outline-secondary flex-grow-1"
                  onClick={handleClear}
                >
                  条件をクリア
                </button>
                <button
                  type="button"
                  className="btn btn-primary flex-grow-1"
                  onClick={handleSearch}
                >
                  <i className="bi bi-search me-1"></i>
                  検索する
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* モーダル群 */}
      <AreaSearchModal
        show={showAreaModal}
        selectedPrefectureIds={selectedPrefectureIds}
        onClose={() => setShowAreaModal(false)}
        onApply={handleAreaApply}
        onImmediateSearch={handleAreaApply}
      />
      <SkillsFilterModal
        show={showSkillsModal}
        selectedSkillIds={selectedSkillIds}
        onClose={() => setShowSkillsModal(false)}
        onApply={handleSkillsApply}
      />
      <OccupationsFilterModal
        show={showOccupationsModal}
        selectedOccupationIds={selectedOccupationIds}
        onClose={() => setShowOccupationsModal(false)}
        onApply={handleOccupationsApply}
      />
      <IndustriesFilterModal
        show={showIndustriesModal}
        selectedIndustryIds={selectedIndustryIds}
        onClose={() => setShowIndustriesModal(false)}
        onApply={handleIndustriesApply}
      />
      <WorkDaysFilterModal
        show={showWorkdaysModal}
        selectedWorkdayTypes={selectedWorkdayTypes}
        onClose={() => setShowWorkdaysModal(false)}
        onApply={handleWorkdaysApply}
      />
      <CharacteristicsFilterModal
        show={showCharacteristicsModal}
        selectedCharacteristicIds={selectedCharacteristicIds}
        onClose={() => setShowCharacteristicsModal(false)}
        onApply={handleCharacteristicsApply}
      />
      <RemoteFilterModal
        show={showRemoteModal}
        selectedRemoteTypes={selectedRemoteTypes}
        onClose={() => setShowRemoteModal(false)}
        onApply={handleRemoteApply}
      />
    </>
  );
};

export default MobileSearchModal;
